import { type ReactNode, useRef, useState } from "react";
import "./MonthlyDateNavigator.scss";
import { DateTime } from "luxon";
import { capitalize } from "@/shared/utils/Strings.ts";
import DateButton from "./DateButton.tsx";
import IconCalendarMonth from "@/shared/components/generic/icons/IconCalendarMonth.tsx";
import { DateStyle } from "@/shared/utils/Dates.ts";
import type { DefinedDateRange } from "./DefinedDateRange.ts";
import { useTranslation } from "react-i18next";
import PopMenu from "@/shared/components/generic/pop-menu/PopMenu.tsx";
import DualMonthPane from "./dual-month-pane/DualMonthPane.tsx";
import PreviousNextButton, {
  type Direction,
} from "@/shared/components/generic/button/PreviousNextButton.tsx";

interface Properties {
  timeZone: string;
  value: DateTime;
  onChange: (d: DateTime) => void;
}

export default function MonthlyDateNavigator(props: Properties): ReactNode {
  const { t } = useTranslation("common");
  const anchorRef = useRef<HTMLDivElement>(null);

  const dateRange: DefinedDateRange = {
    from: props.value.toJSDate(),
    to: props.value.plus({ month: 1 }).minus({ day: 1 }).toJSDate(),
  };

  const [calendarOpen, setCalendarOpen] = useState(false);
  const toggleCalendar = (): void => {
    setCalendarOpen(!calendarOpen);
  };
  const closeCalendar = (): void => {
    setCalendarOpen(false);
  };

  return (
    <div className="monthly-date-navigator" ref={anchorRef}>
      <div className="monthly-date-navigator-grid">
        <div className="from label">{capitalize(t("dateFrom"))}</div>
        <div className="to label">{capitalize(t("dateTo"))}</div>
        <div className="prev-button">
          <PreviousNextButton
            direction={"previous"}
            onClick={move("previous")}
          />
        </div>
        <div className="start-date">
          <DateButton
            value={dateRange.from}
            timeZone={props.timeZone}
            onClick={toggleCalendar}
            icon={IconCalendarMonth}
            dateStyle={DateStyle.DATE}
          />
        </div>
        <div className="end-date">
          <DateButton
            value={dateRange.to}
            timeZone={props.timeZone}
            onClick={toggleCalendar}
            icon={IconCalendarMonth}
            dateStyle={DateStyle.DATE}
          />
        </div>
        <div className="next-button">
          <PreviousNextButton direction={"next"} onClick={move("next")} />
        </div>
      </div>
      <PopMenu
        open={calendarOpen}
        anchorEl={anchorRef.current}
        placement={"bottom"}
        onClose={closeCalendar}
        borderRadius={8}
      >
        <DualMonthPane
          startDate={dateRange.from}
          endDate={dateRange.to}
          timeZone={props.timeZone}
          onChange={(date) => {
            props.onChange(DateTime.fromJSDate(date));
            closeCalendar();
          }}
        />
      </PopMenu>
    </div>
  );

  function move(direction: Direction): () => void {
    return () => {
      props.onChange(
        props.value.plus({ month: direction === "next" ? 1 : -1 }),
      );
    };
  }
}
